import { globalSpinnerController } from '@/components/Spinner';
import { PAGE_PATHS } from '@/constants/url';
import { sharedData } from '@/shares/dataManager';
import { captureException } from '@sentry/browser';
import { getAuth, OAuthProvider } from 'firebase/auth';

const kakaoAuthProvider = new OAuthProvider('oidc.kakao');
kakaoAuthProvider.addScope('account_email');
kakaoAuthProvider.addScope('openid');
kakaoAuthProvider.setCustomParameters({
  prompt: 'consent',
});

export const auth = getAuth();

const generateNonce = (length: number) => {
  const charset =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
};

const makeSha256 = async (message: string) => {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  // 바이트 배열을 16진수 문자열로 변환
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

const getKakaoRedirectUri = () => {
  const currentPathName = window.location.pathname;
  const isAndroidLoginPage = currentPathName.includes(PAGE_PATHS.ANDROID_LOGIN);

  const kakaoRedirectUri = isAndroidLoginPage
    ? `${window.location.origin}/${PAGE_PATHS.ANDROID_LOGIN}`
    : window.location.origin;

  return kakaoRedirectUri;
};

const tryKakaoLogin = async () => {
  globalSpinnerController.showSpinner();

  const kakaoRedirectUri = getKakaoRedirectUri();
  const kakaoRawNonce = generateNonce(32);
  const kakaoCallBackUrl = sharedData.queries.callbackUrl?.href ?? '';
  const kakaoNonce = await makeSha256(kakaoRawNonce);

  const authContext = {
    rawNonce: kakaoRawNonce,
    callbackUrl: kakaoCallBackUrl,
    platform: sharedData.queries.platform || 'web',
  };

  sessionStorage.setItem('kakao_auth_context', JSON.stringify(authContext));

  try {
    window.Kakao.Auth.authorize({
      redirectUri: kakaoRedirectUri,
      nonce: kakaoNonce,
      scope: 'openid, account_email',
      state: kakaoCallBackUrl,
    });
  } catch (error) {
    captureException(error);
  }
};

const tryLinkKakaoAccount = async () => {
  globalSpinnerController.showSpinner();

  const kakaoRedirectUri = getKakaoRedirectUri();
  const kakaoRawNonce = generateNonce(32);
  const kakaoCallBackUrl = sharedData.queries.callbackUrl?.href ?? '';
  const kakaoNonce = await makeSha256(kakaoRawNonce);

  const authContext = {
    rawNonce: kakaoRawNonce,
    callbackUrl: kakaoCallBackUrl,
    platform: sharedData.queries.platform || 'web',
  };

  sessionStorage.setItem('kakao_auth_context', JSON.stringify(authContext));

  try {
    window.Kakao.Auth.authorize({
      redirectUri: kakaoRedirectUri,
      nonce: kakaoNonce,
      scope: 'openid, account_email',
      state: 'link_account',
    });
  } catch (error) {
    captureException(error);
  }
};

const clearKakaoAuthCode = () => {
  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete('code');
  cleanUrl.searchParams.delete('state');
  history.replaceState(null, '', cleanUrl.toString());
};

const getKakaoCredential = async (kakaoAuthCode: string) => {
  clearKakaoAuthCode();

  const kakaoRedirectUri = getKakaoRedirectUri();

  const response = await fetch(
    `${import.meta.env.VITE_CAMERAFI_API_GATEWAY_URL}/studio/api/v1/auth/kakao/token`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code: kakaoAuthCode,
        redirectUri: kakaoRedirectUri,
      }),
    },
  );

  const tokenResponse = await response.json();

  const { accessToken, idToken } = tokenResponse;

  if (!accessToken || !idToken) {
    return undefined;
  }

  const kakaoAuthContext = sessionStorage.getItem('kakao_auth_context');
  const kakaoRawNonce = kakaoAuthContext
    ? JSON.parse(kakaoAuthContext).rawNonce
    : undefined;

  const kakaoCredential = kakaoAuthProvider.credential({
    idToken: idToken,
    accessToken: accessToken,
    rawNonce: kakaoRawNonce,
  });

  return kakaoCredential;
};

export { tryKakaoLogin, tryLinkKakaoAccount, getKakaoCredential };
